import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { JsonSettings } from 'app/model/dataflow/dataset-settings/json-settings';
import { Dataset, DataFlowDSLAdapterRegistry, LinkedService, Metamodel, Owned } from 'shell-models-lib';
import { DataFlowExpressionUtils } from '../expression.util';
import { DataFlowAdapterUtils } from '../adapter.util';
import { DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { FileBasedSettingsAdapter } from './file-based-settings.adapter';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(JsonSettings)
})
export class JsonFormatAdapter extends FileBasedSettingsAdapter {

    public supplementPropsForFabricMigration(
        dataset: Dataset | undefined, linkedService: LinkedService | undefined, props: string[], isSource: boolean,
        isInline?: boolean, datasetSettings?: DataFlowDatasetSettings, _format?: string, wildcardPaths?: DataFlowExpression[]
    ): void {
        if (isInline || !dataset) {
            return;
        }

        props.push(`format: 'json'`);

        DataFlowAdapterUtils.addCommonPropsForFileBasedFormats(dataset, linkedService, props, isSource, isInline, datasetSettings, wildcardPaths);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const jsonDataset = dataset as any;
        if (jsonDataset.compressionCodec) {
            props.push(`compressionType: '${jsonDataset.compressionCodec}'`);
            if (jsonDataset.compressionLevel) {
                props.push(`compressionLevel: ${DataFlowAdapterUtils.formatExpressionValue(jsonDataset.compressionLevel)}`);
            }
        }

        if (jsonDataset.encodingName) {
            props.push(`encodingName: ${DataFlowAdapterUtils.formatExpressionValue(jsonDataset.encodingName)}`);
        }
    }

    protected async toSourceDSL(jsonSettings: JsonSettings): Promise<string[]> {
        const props = [];
        props.push(...await super.toSourceDSL(jsonSettings));
        this._commonPropertiesToDSL(jsonSettings, props);
        const model = jsonSettings.owner as DataFlowSource;
        props.push(`documentForm: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.documentForm)}`);
        if (model.unquotedColumnNames) {
            props.push(`unquotedColumnNames: true`);
        }
        if (model.hasComments) {
            props.push(`hasComments: true`);
        }
        if (model.singleQuoted) {
            props.push(`singleQuoted: true`);
        }
        if (model.backslashEscape) {
            props.push(`backslashEscape: true`);
        }
        return props;
    }

    protected async toSinkDSL(jsonSettings: JsonSettings): Promise<string[]> {
        const props = [];
        props.push(...await super.toSinkDSL(jsonSettings));
        this._commonPropertiesToDSL(jsonSettings, props);
        return props;
    }

    protected async toSourceModel(owner: DataFlowSource, configs: Map<string, string>, handledConfigs: string[]): Promise<JsonSettings> {
        const jsonSettings = await super.toSourceModel(owner, configs, handledConfigs) as JsonSettings;
        this._commonPropertiesToModel(jsonSettings, owner, configs, handledConfigs);
        return jsonSettings;
    }

    protected async toSinkModel(owner: DataFlowSink, configs: Map<string, string>, handledConfigs: string[]): Promise<JsonSettings> {
        const jsonSettings = await super.toSinkModel(owner, configs, handledConfigs) as JsonSettings;
        this._commonPropertiesToModel(jsonSettings, owner, configs, handledConfigs);
        return jsonSettings;
    }

    protected _generateSetting(owner: DataFlowSource | DataFlowSink): JsonSettings {
        return Owned.create<JsonSettings>(JsonSettings.MODEL, owner);
    }

    private _commonPropertiesToDSL(jsonSettings: JsonSettings, props: string[]): void {
        if (jsonSettings.compressionType) {
            props.push(`compressionType: '${jsonSettings.compressionType}'`);
            props.push(`compressionLevel: ${DataFlowExpressionUtils.wrapExpressionConfigValue(jsonSettings.compressionLevel)}`);
        }
        if (jsonSettings.encoding.expression) {
            props.push(`encodingName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(jsonSettings.encoding)}`);
        }
    }

    private _commonPropertiesToModel(jsonSettings: JsonSettings, owner: DataFlowSource | DataFlowSink, configs: Map<string, string>, handledConfigs: string[]): void {
        if (configs.has('compressionType')) {
            jsonSettings.compressionType = DataFlowExpressionUtils.extractStringConfigValue(configs.get('compressionType'));
            handledConfigs.push('compressionType');
        }
        if (configs.has('compressionLevel')) {
            jsonSettings.compressionLevel = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('compressionLevel'), owner);
            handledConfigs.push('compressionLevel');
        }
        if (configs.has('encodingName')) {
            jsonSettings.encoding = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('encodingName'), owner);
            handledConfigs.push('encodingName');
        }
    }
}
